import { Skeleton } from "@/components/shared/Skeleton";
import { StatRow, StatTile } from "@/components/teacher/StatTile";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { skillLabel } from "@/components/teacher/report/reportTokens";
import {
  ReportSkillsDocument,
  ReportStreakDocument,
  StudentHomeworkDocument,
} from "@/generated/graphql";
import { format, useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  HiChevronRight,
  HiOutlineBookOpen,
  HiOutlineChartBar,
  HiOutlineViewGrid,
} from "react-icons/hi";
import { StudentBreadcrumb } from "./StudentBreadcrumb";
import { CLASSROOM_ROOT, type ClassroomScope } from "./classroomScope";
import { useStudentContext } from "./useStudentContext";

/** One student's results: what they have been assigned, and — once it exists — their report. */
export function StudentDetailPage({
  classroomId,
  studentId,
  scope = "teacher",
}: { classroomId: string; studentId: string; scope?: ClassroomScope }) {
  const t = useT();
  const root = CLASSROOM_ROOT[scope];
  const { classroomName, student } = useStudentContext(scope, classroomId, studentId);
  const { data, loading, error } = useQuery(StudentHomeworkDocument, {
    variables: { classroomId, studentId },
  });

  // The report's own page reads these from the cache, so opening it costs nothing extra.
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const skillsQuery = useQuery(ReportSkillsDocument, { variables: { studentId } });
  const streakQuery = useQuery(ReportStreakDocument, { variables: { studentId, timezone } });

  const homework = data?.studentHomework ?? [];
  const completed = homework.filter((item) => item.isComplete).length;
  const overdue = homework.filter((item) => !item.isComplete && item.isLate).length;
  const onTime = homework.filter((item) => item.isComplete && !item.isLate).length;
  const onTimeRate = completed === 0 ? 0 : Math.round((onTime / completed) * 100);

  const subtitle = [student?.username, student?.englishName].filter(Boolean).join(" · ");

  const reportSkills = skillsQuery.data?.reportSkills;
  const currentStreak = streakQuery.data?.streakAnalysis.currentStreak ?? 0;
  const recentPerformance = reportSkills?.recentPerformance;
  const nextFocus = reportSkills?.nextFocus;
  const reportLoading = !skillsQuery.data && !streakQuery.data;

  return (
    <TeacherPage
      title={student?.name ?? t["student.fallbackName"]}
      subtitle={subtitle || undefined}
      embedded={scope === "admin"}
      breadcrumb={
        <StudentBreadcrumb
          scope={scope}
          classroomId={classroomId}
          classroomName={classroomName}
          studentId={studentId}
          studentName={student?.name ?? null}
        />
      }
    >
      {error && (
        <p className="app-card body p-6 text-app-overdue">{t["student.homeworkLoadError"]}</p>
      )}

      <Link
        to={`${root}/$classroomId/students/$studentId/homework`}
        params={{ classroomId, studentId }}
        className="app-card app-card-link block overflow-hidden"
      >
        <div className="flex items-center gap-3 px-5 py-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-app-sky">
            <HiOutlineBookOpen className="h-5 w-5 text-app-ink" />
          </span>
          <span className="app-title flex-1">{t["student.homework"]}</span>
          <HiChevronRight className="h-5 w-5 text-app-ink" />
        </div>
        {loading && homework.length === 0 ? (
          <div className="px-5 pb-5">
            <Skeleton className="h-10" />
          </div>
        ) : (
          <StatRow>
            <StatTile label={t["student.completed"]} value={`${completed}/${homework.length}`} />
            <StatTile label={t["student.onTime"]} value={`${onTimeRate}%`} />
            <StatTile
              label={t["student.overdue"]}
              value={overdue}
              tone={overdue > 0 ? "overdue" : "default"}
            />
          </StatRow>
        )}
      </Link>

      {/* No stat row: a score tile is per slide, and no single number stands in for the grid. */}
      <Link
        to={`${root}/$classroomId/students/$studentId/score`}
        params={{ classroomId, studentId }}
        className="app-card app-card-link block overflow-hidden"
      >
        <div className="flex items-center gap-3 px-5 py-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-app-sky">
            <HiOutlineViewGrid className="h-5 w-5 text-app-ink" />
          </span>
          <span className="app-title flex-1">{t["student.score"]}</span>
          <HiChevronRight className="h-5 w-5 text-app-ink" />
        </div>
      </Link>

      <Link
        to={`${root}/$classroomId/students/$studentId/report`}
        params={{ classroomId, studentId }}
        className="app-card app-card-link block overflow-hidden"
      >
        <div className="flex items-center gap-3 px-5 py-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-app-sky">
            <HiOutlineChartBar className="h-5 w-5 text-app-ink" />
          </span>
          <span className="app-title flex-1">{t["student.report"]}</span>
          <HiChevronRight className="h-5 w-5 text-app-ink" />
        </div>
        {reportLoading ? (
          <div className="px-5 pb-5">
            <Skeleton className="h-10" />
          </div>
        ) : (
          <StatRow>
            <StatTile
              label={t["report.performance.title"]}
              value={recentPerformance == null ? "—" : `${Math.round(recentPerformance)}%`}
              tone={recentPerformance == null ? "empty" : "default"}
            />
            <StatTile
              label={t["student.streak"]}
              value={format(
                currentStreak === 1 ? t["student.streakDayOne"] : t["student.streakDayOther"],
                { count: currentStreak }
              )}
            />
            <StatTile
              label={t["student.nextFocus"]}
              value={nextFocus == null ? "—" : skillLabel(t, nextFocus)}
              tone={nextFocus == null ? "empty" : "default"}
            />
          </StatRow>
        )}
      </Link>
    </TeacherPage>
  );
}
